import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  ArrowRight,
  TrendingUp,
  Users,
  AlertTriangle,
  CheckCircle2,
  Download,
  Filter,
} from "lucide-react";

const students = [
  { initials: "SV", name: "Sanne van Dijk", group: "4 havo", progress: 92, status: "Op koers", tone: "primary" },
  { initials: "OM", name: "Omar Malik", group: "4 havo", progress: 74, status: "Op koers", tone: "primary" },
  { initials: "LB", name: "Lisa Bakker", group: "4 havo", progress: 41, status: "Mogelijke aandacht", tone: "warm" },
  { initials: "JH", name: "Jayden Heuvel", group: "4 havo", progress: 18, status: "Openstaande activiteit", tone: "destructive" },
];

const toneMap: Record<string, { bar: string; badge: string }> = {
  primary: { bar: "bg-primary", badge: "bg-primary/10 text-primary" },
  warm: { bar: "bg-warm", badge: "bg-warm/15 text-warm-foreground" },
  destructive: { bar: "bg-destructive", badge: "bg-destructive/10 text-destructive" },
};

const TeacherReportSection = () => {
  const navigate = useNavigate();

  return (
    <section className="relative py-20 lg:py-28 animate-on-scroll overflow-hidden bg-muted/30">
      <div className="pointer-events-none absolute -top-16 -left-20 w-[480px] h-[480px] rounded-full bg-primary/5 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-80 h-80 rounded-full bg-warm/10 blur-3xl" />

      <div className="container mx-auto max-w-6xl px-4 sm:px-6 relative">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-14 items-center">
          {/* copy */}
          <div>
            <span className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] uppercase text-primary mb-4 px-3 py-1 bg-accent rounded-full">
              <LayoutDashboard className="w-3.5 h-3.5" />
              Voor de docent
            </span>
            <h2 className="text-3xl lg:text-[2.6rem] font-extrabold text-secondary leading-tight">
              Zo houdt de mentor grip op de klas.
            </h2>
            <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
              Zie welke activiteiten zijn afgerond, welke reflecties zijn toegevoegd
              en waar een leerling een vraag heeft. Het overzicht helpt de mentor
              voorbereiden; het gesprek zelf blijft mensenwerk.
            </p>

            <ul className="mt-6 space-y-3">
              {[
                "Voortgang per leerling en per klas",
                "Aandachtspunten zonder automatische conclusies",
                "Reflecties als startpunt voor mentor- en oudergesprekken",
              ].map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm font-semibold text-secondary">
                  <span className="w-6 h-6 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>

            <Button
              onClick={() => navigate("/dashboard")}
              className="mt-8 rounded-full font-semibold gap-2 bg-primary text-primary-foreground hover:bg-primary-hover"
            >
              Bekijk het mentoroverzicht <ArrowRight className="w-4 h-4" />
            </Button>
          </div>

          {/* dashboard mockup */}
          <div className="rounded-[1.75rem] border border-border bg-card shadow-large overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3.5 border-b border-border bg-muted/50">
              <span className="w-3 h-3 rounded-full bg-destructive/40" />
              <span className="w-3 h-3 rounded-full bg-warm/50" />
              <span className="w-3 h-3 rounded-full bg-primary/40" />
              <div className="ml-4 flex-1 max-w-xs">
                <div className="h-6 rounded-full bg-background border border-border flex items-center px-3 text-[11px] text-muted-foreground">
                  portal.studie-connect.nl/dashboard
                </div>
              </div>
            </div>

            <div className="p-5 sm:p-6 space-y-4">
              {/* header */}
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-base sm:text-lg font-extrabold text-secondary leading-tight">
                    Klasoverzicht · 4 havo
                  </h3>
                  <p className="text-xs text-muted-foreground">28 leerlingen · schooljaar 2025/26</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-[11px] font-semibold text-muted-foreground">
                    <Filter className="w-3 h-3" /> Filter
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-2.5 py-1.5 text-[11px] font-semibold text-primary-foreground">
                    <Download className="w-3 h-3" /> Export
                  </span>
                </div>
              </div>

              {/* KPI cards */}
              <div className="grid grid-cols-3 gap-2.5">
                {[
                  { icon: Users, label: "Gem. voortgang", value: "71%", tone: "text-primary" },
                  { icon: TrendingUp, label: "Op koers", value: "22", tone: "text-primary" },
                  { icon: AlertTriangle, label: "Mogelijke aandacht", value: "6", tone: "text-warm" },
                ].map((k) => (
                  <div key={k.label} className="rounded-xl border border-border bg-card p-3 shadow-soft">
                    <k.icon className={`w-4 h-4 ${k.tone} mb-1.5`} />
                    <div className={`text-xl font-extrabold ${k.tone} leading-none`}>{k.value}</div>
                    <div className="text-[10px] text-muted-foreground font-medium mt-1">{k.label}</div>
                  </div>
                ))}
              </div>

              {/* student rows */}
              <div className="rounded-2xl border border-border overflow-hidden">
                <div className="grid grid-cols-[1fr_auto] items-center px-4 py-2.5 bg-muted/50 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  <span>Leerling</span>
                  <span>Voortgang</span>
                </div>
                <ul className="divide-y divide-border">
                  {students.map((s) => {
                    const tone = toneMap[s.tone];
                    return (
                      <li key={s.name} className="grid grid-cols-[1fr_auto] items-center gap-3 px-4 py-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-extrabold text-primary shrink-0">
                            {s.initials}
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-bold text-secondary truncate">{s.name}</div>
                            <span className={`inline-block mt-0.5 rounded-full px-2 py-0.5 text-[9px] font-bold ${tone.badge}`}>
                              {s.status}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-20 sm:w-28 h-2 rounded-full bg-muted overflow-hidden">
                            <div
                              className={`h-full rounded-full ${tone.bar} transition-all duration-700`}
                              style={{ width: `${s.progress}%` }}
                            />
                          </div>
                          <span className="text-xs font-extrabold text-secondary tabular-nums w-8 text-right">
                            {s.progress}%
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeacherReportSection;
